import { z } from 'zod';

const experimentExperienceEnum = z.enum([
	'search-page',
	'quick-find',
	'search-page-space-filter',
	'quick-search',
	'advanced-search',
	'sain',
]);

const layerFunctionBaseSchema = {
	layerType: z.string().startsWith('search_platform_layer_').optional(),
	entity: z.string().optional(),
	subEntity: z.string().optional(),
	hello: z.boolean().optional(),
	isJiraIssueIndexed: z.boolean().optional(),
	confluenceTier: z.string().array().optional(),
	productDataRegions: z.record(z.string(), z.string().array()).optional(),
	experiences: experimentExperienceEnum.array().optional(),
	tenantIds: z.string().array().optional(),
	targetingGateId: z.string().optional(),
	providerId: z.string().optional(),
	connectorSources: z.array(z.string()).optional(),
	integrationAri: z.string().optional(),
};

const layerFunction = z.object(layerFunctionBaseSchema);

const dynamicConfigSchema = z
	.string()
	.transform((str, ctx) => {
		try {
			return JSON.parse(str);
		} catch (e) {
			ctx.addIssue({ code: 'custom', message: 'Invalid JSON' });
			return z.NEVER;
		}
	})
	.pipe(
		z.object({
			...layerFunctionBaseSchema,
			statsigLayerId: z.string().startsWith('search_platform_'),
		}),
	);

const mutuallyExclusiveDynamicConfigSchema = z
	.string()
	.transform((str, ctx) => {
		try {
			return JSON.parse(str);
		} catch (e) {
			ctx.addIssue({ code: 'custom', message: 'Invalid JSON' });
			return z.NEVER;
		}
	})
	.pipe(
		z.object({
			...layerFunctionBaseSchema,
			layerId: z.string(),
			controlId: z.string().optional(),
		}),
	);

export const dynamicExperimentsSchema: z.ZodEffects<
	z.ZodEffects<
		z.ZodArray<z.ZodString, 'many'>,
		{
			statsigLayerId: string;
			layerType?: string | undefined;
			entity?: string | undefined;
			subEntity?: string | undefined;
			hello?: boolean | undefined;
			isJiraIssueIndexed?: boolean | undefined;
			confluenceTier?: string[] | undefined;
			productDataRegions?: Record<string, string[]> | undefined;
			experiences?:
				| (
						| 'search-page'
						| 'quick-find'
						| 'search-page-space-filter'
						| 'quick-search'
						| 'advanced-search'
						| 'sain'
				  )[]
				| undefined;
			tenantIds?: string[] | undefined;
			targetingGateId?: string | undefined;
			providerId?: string | undefined;
			connectorSources?: string[] | undefined;
			integrationAri?: string | undefined;
		}[],
		string[]
	>,
	{
		statsigLayerId: string;
		layerType?: string | undefined;
		entity?: string | undefined;
		subEntity?: string | undefined;
		hello?: boolean | undefined;
		isJiraIssueIndexed?: boolean | undefined;
		confluenceTier?: string[] | undefined;
		productDataRegions?: Record<string, string[]> | undefined;
		experiences?:
			| (
					| 'search-page'
					| 'quick-find'
					| 'search-page-space-filter'
					| 'quick-search'
					| 'advanced-search'
					| 'sain'
			  )[]
			| undefined;
		tenantIds?: string[] | undefined;
		targetingGateId?: string | undefined;
		providerId?: string | undefined;
		connectorSources?: string[] | undefined;
		integrationAri?: string | undefined;
	}[],
	string[]
> = z
	.array(z.string())
	.transform((arrayJSONStrings) => {
		return arrayJSONStrings
			.map((jsonStr) => dynamicConfigSchema.safeParse(jsonStr).data)
			.flatMap((dynamicConfig) => (dynamicConfig ? dynamicConfig : []));
	})
	.refine((dynamicConfig) => dynamicConfig.length); // The schema is invalid if all JSON strings are invalid

export const mutuallyExclusiveBackendSearchLogicParametersSchema: z.ZodEffects<
	z.ZodEffects<
		z.ZodArray<z.ZodString, 'many'>,
		{
			layerId: string;
			layerType?: string | undefined;
			entity?: string | undefined;
			subEntity?: string | undefined;
			hello?: boolean | undefined;
			isJiraIssueIndexed?: boolean | undefined;
			confluenceTier?: string[] | undefined;
			productDataRegions?: Record<string, string[]> | undefined;
			experiences?:
				| (
						| 'search-page'
						| 'quick-find'
						| 'search-page-space-filter'
						| 'quick-search'
						| 'advanced-search'
						| 'sain'
				  )[]
				| undefined;
			tenantIds?: string[] | undefined;
			targetingGateId?: string | undefined;
			providerId?: string | undefined;
			connectorSources?: string[] | undefined;
			integrationAri?: string | undefined;
			controlId?: string | undefined;
		}[],
		string[]
	>,
	{
		layerId: string;
		layerType?: string | undefined;
		entity?: string | undefined;
		subEntity?: string | undefined;
		hello?: boolean | undefined;
		isJiraIssueIndexed?: boolean | undefined;
		confluenceTier?: string[] | undefined;
		productDataRegions?: Record<string, string[]> | undefined;
		experiences?:
			| (
					| 'search-page'
					| 'quick-find'
					| 'search-page-space-filter'
					| 'quick-search'
					| 'advanced-search'
					| 'sain'
			  )[]
			| undefined;
		tenantIds?: string[] | undefined;
		targetingGateId?: string | undefined;
		providerId?: string | undefined;
		connectorSources?: string[] | undefined;
		integrationAri?: string | undefined;
		controlId?: string | undefined;
	}[],
	string[]
> = z
	.array(z.string())
	.transform((arrayJSONStrings) => {
		return arrayJSONStrings
			.map((jsonStr) => mutuallyExclusiveDynamicConfigSchema.safeParse(jsonStr).data)
			.flatMap((dynamicConfig) => (dynamicConfig ? dynamicConfig : []));
	})
	.refine((dynamicConfig) => dynamicConfig.length); // The schema is invalid if all JSON strings are invalid

export type DynamicConfig = z.infer<typeof dynamicConfigSchema>;
export type ExperimentExperience = z.infer<typeof experimentExperienceEnum>;
export type ExperimentExperiences = z.infer<typeof dynamicConfigSchema>['experiences'];
export type LayerFunction = z.infer<typeof layerFunction>;
export type DynamicExperiments = z.infer<typeof dynamicExperimentsSchema>;
export type MutuallyExclusiveBackendSearchLogicParameters = z.infer<
	typeof mutuallyExclusiveBackendSearchLogicParametersSchema
>;
